import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Provider, ProviderFormData } from '../types/referral'
import { getAddressLabels, getDefaultCountry } from '../utils/locale'
import './ProviderForm.css'

const providerSchema = z.object({
  type: z.enum(['internal', 'external']),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  title: z.string().optional(),
  organization: z.string().optional(),
  specialty: z.string().optional(),
  address: z
    .object({
      street: z.string().optional(),
      city: z.string().optional(),
      state: z.string().optional(),
      postalCode: z.string().optional(),
      country: z.string().optional(),
    })
    .optional(),
  phone: z.string().optional(),
  email: z.string().email('Invalid email address').optional().or(z.literal('')),
  healthmailAddress: z.string().email('Invalid email address').optional().or(z.literal('')),
  isActive: z.boolean().optional(),
  notes: z.string().optional(),
})

interface ProviderFormProps {
  initialData?: Provider
  onSubmit: (data: ProviderFormData) => Promise<void>
  onCancel: () => void
  isLoading?: boolean
}

export function ProviderForm({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
}: ProviderFormProps) {
  const addressLabels = getAddressLabels()
  const defaultCountry = getDefaultCountry()

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ProviderFormData>({
    resolver: zodResolver(providerSchema),
    defaultValues: {
      type: initialData?.type || 'external',
      firstName: initialData?.firstName || '',
      lastName: initialData?.lastName || '',
      title: initialData?.title,
      organization: initialData?.organization,
      specialty: initialData?.specialty,
      address: initialData?.address || {
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: defaultCountry,
      },
      phone: initialData?.phone,
      email: initialData?.email,
      healthmailAddress: initialData?.healthmailAddress,
      isActive: initialData?.isActive !== undefined ? initialData.isActive : true,
      notes: initialData?.notes,
    },
  })

  const providerType = watch('type')

  return (
    <form className="provider-form" onSubmit={handleSubmit(onSubmit)}>
      <div className="form-group">
        <label htmlFor="type">
          Type <span className="required">*</span>
        </label>
        <select id="type" {...register('type')}>
          <option value="internal">Internal</option>
          <option value="external">External</option>
        </select>
        {errors.type && (
          <span className="error-message" role="alert">
            {errors.type.message}
          </span>
        )}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="title">Title</label>
          <input
            id="title"
            type="text"
            {...register('title')}
            placeholder="Dr, Prof, etc."
          />
        </div>

        <div className="form-group">
          <label htmlFor="firstName">
            First name <span className="required">*</span>
          </label>
          <input
            id="firstName"
            type="text"
            {...register('firstName')}
            placeholder="First name"
          />
          {errors.firstName && (
            <span className="error-message" role="alert">
              {errors.firstName.message}
            </span>
          )}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="lastName">
            Last name <span className="required">*</span>
          </label>
          <input
            id="lastName"
            type="text"
            {...register('lastName')}
            placeholder="Last name"
          />
          {errors.lastName && (
            <span className="error-message" role="alert">
              {errors.lastName.message}
            </span>
          )}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="organization">Organization</label>
          <input
            id="organization"
            type="text"
            {...register('organization')}
            placeholder="Hospital, clinic, practice name"
          />
        </div>

        <div className="form-group">
          <label htmlFor="specialty">Specialty</label>
          <input
            id="specialty"
            type="text"
            {...register('specialty')}
            placeholder="e.g., Cardiology, Orthopedics"
          />
        </div>
      </div>

      {providerType === 'external' && (
        <>
          <div className="form-group">
            <label htmlFor="addressStreet">{addressLabels.street}</label>
            <input
              id="addressStreet"
              type="text"
              {...register('address.street')}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="addressCity">{addressLabels.city}</label>
              <input
                id="addressCity"
                type="text"
                {...register('address.city')}
              />
            </div>

            <div className="form-group">
              <label htmlFor="addressState">{addressLabels.state}</label>
              <input
                id="addressState"
                type="text"
                {...register('address.state')}
              />
            </div>

            <div className="form-group">
              <label htmlFor="addressPostalCode">{addressLabels.postalCode}</label>
              <input
                id="addressPostalCode"
                type="text"
                {...register('address.postalCode')}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="addressCountry">{addressLabels.country}</label>
            <input
              id="addressCountry"
              type="text"
              {...register('address.country')}
            />
          </div>
        </>
      )}

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="phone">Phone</label>
          <input
            id="phone"
            type="tel"
            {...register('phone')}
            placeholder="+353 1 234 5678"
          />
        </div>

        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            {...register('email')}
            placeholder="provider@example.ie"
          />
          {errors.email && (
            <span className="error-message" role="alert">
              {errors.email.message}
            </span>
          )}
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="healthmailAddress">Healthmail Address</label>
        <input
          id="healthmailAddress"
          type="email"
          {...register('healthmailAddress')}
          placeholder="provider@healthmail.ie"
        />
        {errors.healthmailAddress && (
          <span className="error-message" role="alert">
            {errors.healthmailAddress.message}
          </span>
        )}
      </div>

      <div className="form-group">
        <label>
          <input type="checkbox" {...register('isActive')} />
          Active
        </label>
      </div>

      <div className="form-group">
        <label htmlFor="notes">Notes</label>
        <textarea
          id="notes"
          {...register('notes')}
          rows={3}
          placeholder="Additional notes about this provider"
        />
      </div>

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn-secondary">
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={isLoading}>
          {isLoading ? 'Saving...' : initialData ? 'Update Provider' : 'Create Provider'}
        </button>
      </div>
    </form>
  )
}
